import {
  keys, partition, pickBy, sortBy, isEmpty, isUndefined, sumBy, difference,
} from 'lodash';
import moment from 'moment';
import { getMapMatrix } from '../services';
import { isDeliveryPhase } from './index';
import { getDayCount, getDisplayDateFromString, getEndTimeString,
         getThanksgiving, getChristmasEve, getChristmasDay, parseDateStrict } from './dateUtils';
import {
  RELATED_SPECIFIC_FIELDS,
  NON_COMPARABLE_SPECIFIC_FIELDS,
  SS_PLI_MULTIPLIERS,
  WORK_DAY_HOURS,
  DISPLAY_DATE_TIME, DISPLAY_DATE,
  MULTI_SURFACE_ENABLED,
  TIME_CONSTRAINT_MESSAGES,
  TWD_MESSAGES,
  TOTAL_DAY_LIMIT,
  DELIVERY_BUFFERS,
  RAILS_DATE,
  MOMENT_TIME,
  LHI_SOURCES,
} from 'config/constants';

const dateDay = date => date.day() + 1;

const memberIsCrew = member => member.assignee.kind === 'Crew';

const memberHasMismatch = member => member.assignee.specificMismatch;

export const membersContainCrews = members => members.some(memberIsCrew);

export const membersContainNonCrews = members => members.some(m => !memberIsCrew(m));

// IFI phases only: show just the assigned estimator (assessments) and the assigned
// installer's crews (assessments/installations/workorders) by default. The rest of the
// qualifying estimators/crews are hidden unless "Show Mismatched Estimators/Crews" is on.
// A phase with no estimator/installer assigned yet filters nothing (nothing to match against).
export const membersMatchAssignment = (
  members, phaseSource, showMismatched, phaseEstimatorId, phaseInstallerId,
) => {
  if (phaseSource !== 'IFI') return members;
  return members.filter((m) => {
    const { kind, id, installer_id: installerId } = m.assignee;
    if (kind === 'Estimator') return showMismatched || !phaseEstimatorId || id === phaseEstimatorId;
    if (kind === 'Crew') return showMismatched || !phaseInstallerId || installerId === phaseInstallerId;
    return true;
  });
};

const separateCrews = members => partition(members, m => memberIsCrew(m));

const separateMismatches = members => partition(members, m => memberHasMismatch(m));

const getSpecificsToMatch = phaseSpecifics =>
  keys(pickBy(phaseSpecifics)).filter(k => !NON_COMPARABLE_SPECIFIC_FIELDS.includes(k));

const getTimeAddedByQty = (phaseLineItems, itemNumbers, multiplier) => {
  let total = 0;
  phaseLineItems.forEach((i) => {
    const timeAdded = itemNumbers.includes(i.item_number) ?
      parseInt(i.quantity, 10) * multiplier : 0;
    total += timeAdded;
  });
  return total;
};

const getTimeAdded = (key, phaseLineItems, sqft) => {
  const { itemNumbers, multiplier } = SS_PLI_MULTIPLIERS[key];
  if (key === 'need_move_furniture') return sqft * multiplier;
  if (isEmpty(itemNumbers) || !multiplier) return multiplier;
  return getTimeAddedByQty(phaseLineItems, itemNumbers, multiplier);
};

const getPhaseSpecificTime = (phaseSpecificsToMatch, phaseLineItems, sqft) => {
  let total = 0;
  phaseSpecificsToMatch.forEach((s) => {
    const timeAdded = s in SS_PLI_MULTIPLIERS ? getTimeAdded(s, phaseLineItems, sqft) : 0;
    total += timeAdded;
  });
  return total;
};

const determineSurfaceJobLength = (specific, sqft) => {
  if (specific === 0) return 0; // avoids infinity case
  return (sqft * WORK_DAY_HOURS) / specific;
};

const determineBaseJobLength = (crewSpecifics, pendingSurface, sqft) => {
  const { hardwood_sf_per_day, carpet_sf_per_day, tile_sf_per_day, laminate_sf_per_day,
    vinyl_sf_per_day, vinyl_plank_sf_per_day, backsplash_sf_per_day } = crewSpecifics;
  switch (pendingSurface) {
    case 'Hardwood':
      return determineSurfaceJobLength(hardwood_sf_per_day, sqft);
    case 'Carpet':
      return determineSurfaceJobLength(carpet_sf_per_day, sqft);
    case 'Tile':
      return determineSurfaceJobLength(tile_sf_per_day, sqft);
    case 'Laminate':
      return determineSurfaceJobLength(laminate_sf_per_day, sqft);
    case 'Vinyl':
      return determineSurfaceJobLength(vinyl_sf_per_day, sqft);
    case 'Vinyl Plank':
      return determineSurfaceJobLength(vinyl_plank_sf_per_day, sqft);
    case 'Backsplash':
      return determineSurfaceJobLength(backsplash_sf_per_day, sqft);
    default:
      return 0;
  }
};

const determineFullDays = hours => Math.floor(hours / WORK_DAY_HOURS);

const determineDaysFromHours = (hours) => {
  if (WORK_DAY_HOURS > hours) return 1;
  return determineFullDays(hours) + (hours % WORK_DAY_HOURS > 0 ? 1 : 0);
};

const determineAddedHours = hours => Math.ceil(hours - (WORK_DAY_HOURS * determineFullDays(hours)));

export const determineProjectedCompletion = (
  hours, days, startDate, startTime, defaultStartTime,
) => {
  const date = parseDateStrict(startDate, [DISPLAY_DATE, RAILS_DATE]);
  const start = moment(
    `${date.format(DISPLAY_DATE)} ${startTime}`,
    `${DISPLAY_DATE} ${MOMENT_TIME}`,
    true,
  );
  const normalizedDefaultStart = defaultStartTime || startTime;

  if (!date.isValid() || !start.isValid()) return '';

  if (days <= 1) {
    return start.clone().add(Math.ceil(hours), 'hours').format(DISPLAY_DATE_TIME);
  }

  const lastDayStart = moment(
    `${date.clone().add(days - 1, 'days').format(DISPLAY_DATE)} ${normalizedDefaultStart}`,
    `${DISPLAY_DATE} ${MOMENT_TIME}`,
    true,
  );
  return lastDayStart.add(determineAddedHours(hours), 'hours').format(DISPLAY_DATE_TIME);
};

const getCalJobLengthAttrs = (crews, phaseSpecificsToMatch, pendingAppt) => {
  const { phase: { phase_line_items, sqft, surface_type: { name } },
    start_date, arrival_start_time } = pendingAppt;
  const phaseSpecificTime = getPhaseSpecificTime(phaseSpecificsToMatch, phase_line_items, sqft);
  return crews.map((c) => {
    const { assignee: { crew_specific, default_start_time } } = c;
    const projectedJobLength = determineBaseJobLength(crew_specific, name, sqft)
      + phaseSpecificTime;
    const projectedJobDays = determineDaysFromHours(projectedJobLength);
    const projectedCompletion = determineProjectedCompletion(
      projectedJobLength,
      projectedJobDays,
      start_date,
      arrival_start_time,
      default_start_time,
    );
    return { ...c, projectedJobLength, projectedJobDays, projectedCompletion };
  });
};

const getSuggestionJobLengthAttrs = (crews, specificsToMatch, phase) => {
  const { phase_line_items, sqft, surface_type: { name: surfaceType } } = phase;
  const specificsTime = getPhaseSpecificTime(specificsToMatch, phase_line_items, sqft);
  return crews.map((c) => {
    const { assignee: { crew_specific, delivery_dedicated } } = c;
    let jobLengthAttrs = { projectedJobLength: null, projectedJobDays: null };
    if (!delivery_dedicated) {
      const projectedJobLength = Math.round((specificsTime +
        determineBaseJobLength(crew_specific, surfaceType, sqft)) * 10) / 10;
      jobLengthAttrs = {
        projectedJobLength,
        projectedJobDays: determineDaysFromHours(projectedJobLength),
      };
    }
    return { ...c, ...jobLengthAttrs };
  });
};

const overlapExists = (pendingStart, pendingEnd, existingStart, existingEnd) => {
  const pendingStartOverlap = pendingStart.isSameOrAfter(existingStart) &&
    pendingStart.isSameOrBefore(existingEnd);
  const pendingEndOverlap = pendingEnd.isSameOrAfter(existingStart) &&
    pendingEnd.isSameOrBefore(existingEnd);
  const existingStartOverlap = existingStart.isSameOrAfter(pendingStart) &&
    existingStart.isSameOrBefore(pendingEnd);
  const existingEndOverlap = existingEnd.isSameOrAfter(pendingStart) &&
    existingEnd.isSameOrBefore(pendingEnd);
  return pendingStartOverlap || pendingEndOverlap || existingStartOverlap || existingEndOverlap;
};

const doDatesOverlap = (pendingAppt, existingAppt) => {
  const pendingStart = moment(pendingAppt.start_date);
  const pendingEnd = moment(pendingAppt.end_date);
  const existingStart = moment(existingAppt.start_date);
  const existingEnd = moment(existingAppt.end_date);
  return overlapExists(pendingStart, pendingEnd, existingStart, existingEnd);
};

export const doTimesOverlap = (pendingAppt, existingAppt) => {
  const { start_date: pendingDate, arrival_start_time: pendingTime,
          projectedCompletion } = pendingAppt;
  const { start_date: existingDate, arrival_start_time: existingTime,
          projected_completion } = existingAppt;
  if (!(projected_completion || '')) return true;
  const pendingStart = moment(`${pendingDate} ${pendingTime}`);
  const pendingEnd = moment(projectedCompletion);
  const existingStart = moment(`${existingDate} ${existingTime}`);
  const existingEnd = moment(projected_completion);
  return overlapExists(pendingStart, pendingEnd, existingStart, existingEnd);
};

export const determineBookedDay = overlappingAppts => overlappingAppts.length > 1;

export const multiDayApptsExist = apptLengths => apptLengths.some(n => n > 1);

export const pendingMultiDayOverlap = (pendingDayCount, overlappingAppts) =>
  (pendingDayCount > 1 && overlappingAppts.length > 0);

const determineOverlappingDates = (pendingAppt, crewSchedule) => {
  if (crewSchedule < 1) return false;
  const values = [];
  crewSchedule.forEach(a => values.push(doDatesOverlap(pendingAppt, a)));
  return values.some(v => v);
};

export const getOverlappingAppts = (pendingAppt, crewSchedule) => crewSchedule.filter(a =>
  doDatesOverlap(pendingAppt, a));

const getApptLength = appt => getDayCount(appt.start_date, appt.end_date);

export const getScheduledLengths = (crewSchedule) => {
  const lengths = [];
  crewSchedule.forEach(a => lengths.push(getApptLength(a)));
  return lengths;
};

const determineOverlappingDatesSurfacesMatch = (pendingAppt, crewSchedule) =>
  !getOverlappingAppts(pendingAppt, crewSchedule).some(o =>
    (((o.phase || {}).surface_type || {}).name || '') !== pendingAppt.phase.surface_type.name);

const getOverlappingDatesAttrs = (crews, pendingAppt) => {
  const overlapDeterminedCrews = [];
  crews.forEach((c) => {
    const { ss_availability: { appts, pto } } = c;
    const crewSchedule = appts.concat(pto);
    const overlappingDates = determineOverlappingDates(pendingAppt, crewSchedule);
    const overlappingDatesSurfacesMismatch = overlappingDates ?
      !determineOverlappingDatesSurfacesMatch(pendingAppt, crewSchedule) : false;
    overlapDeterminedCrews.push({ ...c, overlappingDates, overlappingDatesSurfacesMismatch });
  });
  return overlapDeterminedCrews;
};

const hasSqftCapacity = (sfPerDay, pendingSqft, pendingDayCount, scheduledSqft) => {
  if (pendingDayCount < 2) return sfPerDay - (pendingSqft + scheduledSqft) >= 0;
  return sfPerDay * pendingDayCount >= pendingSqft;
};

const isPto = appt => isUndefined(appt.phase);

export const ptoScheduled = crewSchedule => crewSchedule.some(a => isPto(a));

const doSurfacesMatch = (existingSurface, pendingSurface, sameDayMultiSurface) =>
  (MULTI_SURFACE_ENABLED.includes(pendingSurface) && pendingSurface === existingSurface) ||
  (sameDayMultiSurface && !difference(
    [existingSurface, pendingSurface], MULTI_SURFACE_ENABLED,
  ).length);

export const surfaceMismatch = (crewSchedule, pendingSurface, sameDayMultiSurface) => {
  if (ptoScheduled(crewSchedule)) return false;
  return crewSchedule.some(a =>
    !doSurfacesMatch(a.phase.surface_type.name, pendingSurface, sameDayMultiSurface));
};

export const hasCapacityForSqft = (
  pendingSurface, pendingSqft, pendingDayCount, crewSchedule, crewSpecifics,
) => {
  if (ptoScheduled(crewSchedule) || surfaceMismatch(
    crewSchedule, pendingSurface, crewSpecifics.same_day_multi_surface,
  )) return false;
  const { hardwood_sf_per_day, carpet_sf_per_day, tile_sf_per_day, laminate_sf_per_day,
    vinyl_sf_per_day, vinyl_plank_sf_per_day, backsplash_sf_per_day } = crewSpecifics;
  const scheduledSqft = sumBy(crewSchedule, a => a.phase.sqft);
  switch (pendingSurface) {
    case 'Hardwood':
      return hasSqftCapacity(hardwood_sf_per_day, pendingSqft, pendingDayCount, scheduledSqft);
    case 'Carpet':
      return hasSqftCapacity(carpet_sf_per_day, pendingSqft, pendingDayCount, scheduledSqft);
    case 'Tile':
      return hasSqftCapacity(tile_sf_per_day, pendingSqft, pendingDayCount, scheduledSqft);
    case 'Laminate':
      return hasSqftCapacity(laminate_sf_per_day, pendingSqft, pendingDayCount, scheduledSqft);
    case 'Vinyl':
      return hasSqftCapacity(vinyl_sf_per_day, pendingSqft, pendingDayCount, scheduledSqft);
    case 'Vinyl Plank':
      return hasSqftCapacity(vinyl_plank_sf_per_day, pendingSqft, pendingDayCount, scheduledSqft);
    case 'Backsplash':
      return hasSqftCapacity(backsplash_sf_per_day, pendingSqft, pendingDayCount, scheduledSqft);
    default:
      return false;
  }
};

const getSuggestionMapAttr = (suggestionObj, deliverySuggestionObj, phaseId, storeId) => {
  const suggestions = isEmpty(deliverySuggestionObj) ? suggestionObj.suggestions :
    suggestionObj.suggestions.concat(deliverySuggestionObj.suggestions);
  const editedSuggestions = suggestions.map((s) => {
    const { sameDayAppt = [], type } = s;
    if (!isEmpty(sameDayAppt)) {
      let originId = null;
      let destinationId = null;
      if (type === 'delivery') {
        originId = sameDayAppt[0].phase.store.id;
        destinationId = storeId;
      } else {
        originId = sameDayAppt.phase.id;
        destinationId = phaseId;
      }
      return getMapMatrix({ originId, destinationId, type }).then(m => ({ ...s, mapMatrix: m }));
    }
    return { ...s, mapMatrix: {} };
  });
  return Promise.all(editedSuggestions).then(allSuggestions =>
    ({
      deliverySuggestions: allSuggestions.filter(s => s.type === 'delivery'),
      suggestions: allSuggestions.filter(s => s.type === 'install'),
    })).catch(() => ({ ...suggestionObj, ...deliverySuggestionObj, ...{} }));
};

const warningPrefix = warning => (warning ? ', ' : 'WARNING: ');

export const maxDistanceExceeded = (duration, maxTravelTime) => duration / 60 > maxTravelTime;

const existingWorkorder = appts => appts.some(a => a.phase.type === 'Workorder');

export const existingApptsRelate = (pendingId, appts, relatedPhases = []) => appts.length &&
  appts.every(a => !isEmpty(a.phase || {}) &&
  ((a.phase || {}).id === pendingId ||
    relatedPhases.some(p => p.id === (a.phase || {}).id)
  ));

const futureMultiOverlap = (date, projectedJobDays, soonestLaterAppt) => {
  const { start_date, end_date } = soonestLaterAppt;
  return (projectedJobDays > 1 || start_date !== end_date) &&
    date.clone().add(projectedJobDays - 1, 'days').isSameOrAfter(moment(start_date));
};

export const determineCellBlock = (
  date, daySched, assignee, pendingAppt, twdData, allSched, isCcs,
) => {
  if (assignee.specificMismatch || false) return 'WARNING: crew specifics do not match phase specifics';
  let warning = '';
  const { appts = [], pto = {} } = daySched;
  const filteredAllSched = allSched && allSched.length ?
    allSched.filter(a => a.id > 0 && a.id !== pendingAppt.id) : [];
  const filteredAppts = appts.length ? appts.filter(a => a.id > 0 && a.id !== pendingAppt.id) : [];
  if (!isUndefined(pto) && !isEmpty(pto)) {
    warning += warningPrefix(warning) + TIME_CONSTRAINT_MESSAGES[0];
    if (isCcs) return warning;
  }
  const { relatedPhases, phase: { id, sqft, zip_code, surface_type: { name } } } = pendingAppt;
  const { crew_specific = {}, projectedCompletion, projectedJobDays, covered_zip_codes } = assignee;
  const { twd = {}, twdZip = '' } = twdData;
  if (isUndefined(twd) || isEmpty(twd)) {
    if (twdZip && !covered_zip_codes.includes(zip_code)) {
      warning += warningPrefix(warning) + TWD_MESSAGES[0];
      if (isCcs) return warning;
    }
  } else if (!twd.traveling_zip_codes.includes(zip_code)) {
    warning += warningPrefix(warning) + TWD_MESSAGES[1];
    if (isCcs) return warning;
  }
  if (existingWorkorder(filteredAppts)) {
    warning += `${warningPrefix(warning)}workorder scheduled`;
    if (isCcs) return warning;
  }
  const apptsRelate = existingApptsRelate(id, filteredAppts, relatedPhases);
  const isSurfaceMismatch = surfaceMismatch(
    filteredAppts, name, crew_specific.same_day_multi_surface,
  );
  const laterAppts = !filteredAllSched.length ? [] : filteredAllSched
    .filter(a => date.isSameOrBefore(a.start_date))
    .sort((a, b) => moment(a.start_date).diff(b.start_date));
  const existingDQ = !isEmpty(filteredAppts) && (!apptsRelate || isSurfaceMismatch);
  if (laterAppts.length &&
      !existingApptsRelate(id, [laterAppts[0]], relatedPhases) &&
      futureMultiOverlap(date, projectedJobDays, laterAppts[0])) {
    warning += warningPrefix(warning) + TIME_CONSTRAINT_MESSAGES[1];
  } else if (existingDQ) {
    if (multiDayApptsExist(getScheduledLengths(filteredAppts)) ||
        pendingMultiDayOverlap(projectedJobDays, filteredAppts)) {
      warning += warningPrefix(warning) + TIME_CONSTRAINT_MESSAGES[1];
    }
    if (determineBookedDay(filteredAppts)) {
      warning += warningPrefix(warning) + TIME_CONSTRAINT_MESSAGES[2];
    }
    /*
    * RegExp object and function used to check and see if the warning already
    * contains any of the messages above.
    */
    if (!(new RegExp(TIME_CONSTRAINT_MESSAGES.join('|')).test(warning)) &&
        filteredAppts.length &&
        doTimesOverlap({ ...pendingAppt, projectedCompletion }, filteredAppts[0])) {
      warning += warningPrefix(warning) + TIME_CONSTRAINT_MESSAGES[3];
    }
    if (isSurfaceMismatch) warning += warningPrefix(warning) + TIME_CONSTRAINT_MESSAGES[4];
    if (!isSurfaceMismatch && !hasCapacityForSqft(
      name, sqft, projectedJobDays, filteredAppts, crew_specific,
    )) warning += `${warningPrefix(warning)}over crew capacity`;
  }
  return warning;
};

export const isTimeBlocked = ssBlockage => ssBlockage === 'Blocked due to: times overlap';

export const determineAdjustedStartTime = (existingAppt, crew) => {
  const { arrival_start_time, start_date, projected_completion } = existingAppt;
  const { default_start_time, projectedJobLength } = crew;
  if (!(projected_completion || '')) return arrival_start_time;
  const startOfDayMoment = moment(`${start_date} ${default_start_time}`);
  const existingArrivalMoment = moment(`${start_date} ${arrival_start_time}`);
  const existingCompleteMoment = moment(projected_completion);
  if (existingArrivalMoment.diff(startOfDayMoment, 'hours', true) - 1 > projectedJobLength) return default_start_time;
  return existingCompleteMoment.add(1, 'hour').format('HH:mm');
};

const getApptRange = appt => moment.range(moment(appt.start_date), moment(appt.end_date));

const rangeContainsDate = (appt, date) => getApptRange(appt).contains(date);

const coveredBySchedule = (appts = [], date) =>
  appts.length > 0 && appts.some(a => rangeContainsDate(a, date));

const getNextOpeningAttr = (crews, defaultCalStart) => {
  const calStartDate = moment(defaultCalStart).format(DISPLAY_DATE);
  const nextOpeningDeterminedCrews = [];
  crews.forEach((c) => {
    const { projectedJobDays, ss_availability: { appts, pto } } = c;
    const crewSchedule = appts.concat(pto);
    const date = parseDateStrict(calStartDate, DISPLAY_DATE);
    let nextOpening = '';
    let availableDays = 0;
    while (availableDays < projectedJobDays) {
      if (coveredBySchedule(crewSchedule, date)) {
        nextOpening = '';
        date.add(1, 'day');
        availableDays = 0;
      } else if (!nextOpening) {
        nextOpening = date.format(DISPLAY_DATE);
        date.add(1, 'day');
        availableDays += 1;
      } else {
        date.add(1, 'day');
        availableDays += 1;
      }
    }
    nextOpeningDeterminedCrews.push({ ...c, nextOpening });
  });
  return nextOpeningDeterminedCrews;
};

const isTravelZip = (travelZips, phaseZip) => travelZips.includes(phaseZip);

const coveredByTravel = (twd, date, crew, zipCode) => {
  const { covered_zip_codes, traveling_zip_codes } = crew;
  if (twd.length < 1 || traveling_zip_codes.length < 1) return false;
  if (isTravelZip(traveling_zip_codes, zipCode)) {
    const applicableTwd = twd.find(t => rangeContainsDate(t, date)) || {};
    return !covered_zip_codes.includes(zipCode) &&
      (isEmpty(applicableTwd) || !applicableTwd.traveling_zip_codes.includes(zipCode));
  }
  return coveredBySchedule(twd, date);
};

const getTimeDiff = (start, end, unit, includeDecimal) =>
  end.diff(start, unit, includeDecimal);

const noTimeInDay = (appt, crew) => {
  const { arrival_start_time, start_date, projected_completion } = appt;
  if (!(projected_completion || '')) return true;
  const { default_start_time, projectedJobLength = 0 } = crew;
  const startOfDayMoment = moment(`${start_date} ${default_start_time}`);
  const existingArrivalMoment = moment(`${start_date} ${arrival_start_time}`);
  const existingCompleteMoment = moment(projected_completion);
  const endOfDayMoment = startOfDayMoment.clone().add(WORK_DAY_HOURS, 'hours');
  const beforeTime = getTimeDiff(startOfDayMoment, existingArrivalMoment, 'hours', true);
  const afterTime = getTimeDiff(existingCompleteMoment, endOfDayMoment, 'hours', true);
  return (afterTime - 1 < projectedJobLength) && (beforeTime - 1 < projectedJobLength);
};

const hasSuggestionCap = (capacity, existingSqft, pendingSqft) =>
  (capacity - (existingSqft + pendingSqft) >= 0);

/*
 * TODO: this WILL need to be redone if we ever allow different surface types to
 * be scheduled on the same day. As things are currently set up, we only have
 * the means to measure capacity if appt surfaces match, as there is no formula
 * for comparing capacity of one surface to another. ANYWHERE capacity is
 * evaluated, for that matter, will need to be considered.
 */
const willBeOverCap = (surfaceType, crewSpecifics, existingSqft, pendingSqft) => {
  const { hardwood_sf_per_day, carpet_sf_per_day, tile_sf_per_day, laminate_sf_per_day,
    vinyl_sf_per_day, vinyl_plank_sf_per_day, backsplash_sf_per_day } = crewSpecifics;
  switch (surfaceType) {
    case 'Hardwood':
      return !hasSuggestionCap(hardwood_sf_per_day, existingSqft, pendingSqft);
    case 'Carpet':
      return !hasSuggestionCap(carpet_sf_per_day, existingSqft, pendingSqft);
    case 'Tile':
      return !hasSuggestionCap(tile_sf_per_day, existingSqft, pendingSqft);
    case 'Laminate':
      return !hasSuggestionCap(laminate_sf_per_day, existingSqft, pendingSqft);
    case 'Vinyl':
      return !hasSuggestionCap(vinyl_sf_per_day, existingSqft, pendingSqft);
    case 'Vinyl Plank':
      return !hasSuggestionCap(vinyl_plank_sf_per_day, existingSqft, pendingSqft);
    case 'Backsplash':
      return !hasSuggestionCap(backsplash_sf_per_day, existingSqft, pendingSqft);
    default:
      return true;
  }
};

export const isAvailableForDay = (date, dailyAvailability) => {
  const { morning = [], afternoon = [] } = dailyAvailability;
  return morning.concat(afternoon).includes(dateDay(date));
};

const deliveryLimitByAvailability = (date, dailyAvailability) => {
  const { morning = [], afternoon = [] } = dailyAvailability;
  const day = dateDay(date);
  if (morning.includes(day) && afternoon.includes(day)) return 4;
  else if (morning.includes(day) || afternoon.includes(day)) return 2;
  return 0;
};

// const isAvailableForDayByType = (type, date, crew) =>
// crew[`${type}_availability`].includes(date.day() + 1);

const sortApptsByDelivery = (appts, date) => {
  const sortedAppts = { deliveryAppts: [], otherAppts: [] };
  if (!appts.length) return sortedAppts;
  const sameDayAppts = appts.filter(a => rangeContainsDate(a, date));
  if (!sameDayAppts.length) return sortedAppts;
  const deliveryAppts = [];
  const otherAppts = [];
  sameDayAppts.forEach((a) => {
    if (['delivery', 'delivery_hold'].includes(a.kind)) deliveryAppts.push(a);
    else otherAppts.push(a);
  });
  return { deliveryAppts, otherAppts };
};

const determineDeliveryLimit = (dailyAvailability, appts, date) => {
  let deliveryLimit = deliveryLimitByAvailability(date, dailyAvailability);
  const { otherAppts } = sortApptsByDelivery(appts, date);
  if (deliveryLimit === 0 || !otherAppts.length) return deliveryLimit;
  if (otherAppts.length > 1) return 0;
  else if (otherAppts.length === 1) deliveryLimit -= 2;
  return deliveryLimit;
};

const deliveryStartTime = (dailyAvailability, crewSched, date) => {
  const { appts } = crewSched;
  const { deliveryAppts = [] } = sortApptsByDelivery(appts, date);
  const existingDeliveryCount = deliveryAppts.length;
  const { morning, afternoon } = dailyAvailability;
  const day = dateDay(date);
  const deliveryLimit = determineDeliveryLimit(dailyAvailability, appts, date);
  if (deliveryLimit < 1 || existingDeliveryCount + 1 > deliveryLimit) return null;
  let startTime = '08:00';
  if (morning.includes(day) && afternoon.includes(day)) {
    startTime = existingDeliveryCount > 1 ? '13:00' : '8:00';
  } else if (afternoon.includes(day)) startTime = '13:00';
  return startTime;
};

const coveredByApptsForDelivery = (dailyAvailability, appts, date) => {
  const sameDayAppts = appts.filter(a => rangeContainsDate(a, date));
  const deliveryAppts = [];
  sameDayAppts.forEach(a => (['delivery', 'delivery_hold'].includes(a.kind) ? deliveryAppts.push(a) : null));
  const deliveryLimit = determineDeliveryLimit(dailyAvailability, appts, date);
  return deliveryAppts.length + 1 > deliveryLimit;
};

const coveredByAppts = (appts, date, crew, phaseData) => {
  const sameDayAppts = appts.filter(a => rangeContainsDate(a, date));
  const { id, relatedPhases, surfaceType, sqft } = phaseData;
  const { projectedJobDays, crew_specific: specifics, crew_specific: {
    same_day_multi_surface: multiSurfaceAllowed,
  } } = crew;
  if (appts.length < 1 ||
      !coveredBySchedule(appts, date) ||
      sameDayAppts.length < 1 ||
      existingApptsRelate(id, sameDayAppts, relatedPhases)) return false;
  if (existingWorkorder(sameDayAppts) ||
      determineBookedDay(sameDayAppts) ||
      !doSurfacesMatch(
        sameDayAppts[0].phase.surface_type.name, surfaceType, multiSurfaceAllowed,
      ) ||
      getApptLength(sameDayAppts[0]) > 1 ||
      projectedJobDays > 1 ||
      willBeOverCap(surfaceType, specifics, sameDayAppts[0].phase.sqft, sqft) ||
      noTimeInDay(sameDayAppts[0], crew)) return true;
  return false;
};

const dateAlreadySelected = (date, openings) => openings.includes(date);

// const overlapsWeekend = date => [0, 6].includes(date.day());

const overlapsHoliday = (date) => {
  const holidays = [getThanksgiving(), getChristmasEve(), getChristmasDay()];
  return holidays.includes(date);
};

const blockedByVendor = (appts, date, { source }) => {
  const sameDayAppts = appts.filter(a => rangeContainsDate(a, date));
  if (!sameDayAppts.length) return false;
  if (LHI_SOURCES.includes(source)) {
    return sameDayAppts.some(a => !LHI_SOURCES.includes(a.phase.source));
  }
  return sameDayAppts.some(a => a.phase.source !== source);
};

const isDeliveryDayCovered = (crewSched, dateData, crew, phaseData) => {
  const { newDate, openings } = dateData;
  const { appts, pto, twd } = crewSched;
  const { daily_availability } = crew;
  if (/*
      not using this for now
      !isAvailableForDayByType('delivery', newDate, crew) ||
      */
      blockedByVendor(appts, newDate, phaseData) ||
      dateAlreadySelected(newDate.clone().format('MM/DD/YYYY'), openings) ||
      overlapsHoliday(newDate.clone().format('MM/DD/YYYY')) ||
      !isAvailableForDay(newDate, daily_availability) ||
      coveredBySchedule(pto, newDate) ||
      coveredByTravel(twd, newDate, crew, phaseData.zip_code) ||
      coveredByApptsForDelivery(daily_availability, appts, newDate)) return true;
  return false;
};

const isDayCovered = (crewSched, dateData, crew, phaseData) => {
  const { newDate, openings } = dateData;
  const { appts, pto, twd } = crewSched;
  if (blockedByVendor(appts, newDate, phaseData) ||
      dateAlreadySelected(newDate.clone().format('MM/DD/YYYY'), openings) ||
      overlapsHoliday(newDate.clone().format('MM/DD/YYYY')) ||
      !isAvailableForDay(newDate, crew.daily_availability) ||
      coveredBySchedule(pto, newDate) ||
      coveredByTravel(twd, newDate, crew, phaseData.zip_code) ||
      coveredByAppts(appts, newDate, crew, phaseData)) return true;
  return false;
};

const getSuggestionStartTime = (date, defaultStart, projDays, projLength, appts) => {
  if (projDays > 1 || appts.length < 1 || !coveredBySchedule(appts, date)) return defaultStart;
  const sameDayAppt = appts.find(a => rangeContainsDate(a, date));
  return determineAdjustedStartTime(
    sameDayAppt, { default_start_time: defaultStart, projectedJobLength: projLength },
  );
};

const noMoreTwd = (date, twd, phaseZip) => {
  if (!twd.length) return false;
  const currTwd = twd.find(d => d.traveling_zip_codes.includes(phaseZip)) || null;
  if (!currTwd) return false;
  return date.isAfter(moment(currTwd.end_date));
};

const findSameDayDeliveries = (date, appts) => (appts.length > 0 ?
  appts.filter(a =>
    getDisplayDateFromString(a.start_date, 'YYYY-MM-DD') ===
    getDisplayDateFromString(date, DISPLAY_DATE) &&
    ['delivery', 'delivery_hold'].includes(a.kind),
  ) || [] : []);

const findSameDayExisting = (date, appts) => (appts.length > 0 ?
  appts.find(a =>
    getDisplayDateFromString(a.start_date, 'YYYY-MM-DD') ===
    getDisplayDateFromString(date, DISPLAY_DATE),
  ) || {} : {});

const determineDeliveryNoAvailability = (totalDays, nextOpenings) =>
  nextOpenings.length >= 5 || totalDays >= TOTAL_DAY_LIMIT;

const getSuggestionDeliveryOpenings = (crews, date, phaseData) => {
  const givenDate = moment(date).format(DISPLAY_DATE);
  const nextOpeningDeterminedCrews = [];
  crews.forEach((c) => {
    const { twd, ss_availability, assignee } = c;
    if (!assignee.delivery_dedicated) return;
    const nextOpenings = [];
    const startDate = parseDateStrict(givenDate, DISPLAY_DATE);
    let noMoreAvailability = false;
    while (!noMoreAvailability && nextOpenings.length < 5) {
      if (nextOpenings.length > 0) startDate.add(1, 'day');
      const newDate = startDate.clone();
      let totalDays = 0;
      let nextOpening = '';
      let availableDays = 0;
      while (!availableDays && totalDays < TOTAL_DAY_LIMIT) {
        totalDays += 1;
        if (isDeliveryDayCovered(
            { ...ss_availability, twd },
            { newDate, openings: nextOpenings.map(o => o.opening) },
            assignee,
            phaseData)) {
          nextOpening = '';
          availableDays = 0;
          newDate.add(1, 'day');
        } else {
          nextOpening = newDate.format(DISPLAY_DATE);
          availableDays += 1;
          newDate.add(1, 'day');
        }
      }
      if (totalDays <= TOTAL_DAY_LIMIT) {
        if (noMoreTwd(newDate, twd, phaseData.zip_code)) noMoreAvailability = true;
        if (!noMoreAvailability && nextOpening) {
          nextOpenings.push({
            type: 'delivery',
            opening: nextOpening,
            sameDayAppt: findSameDayDeliveries(nextOpening, ss_availability.appts),
            startTime: deliveryStartTime(
            assignee.daily_availability,
            { ...ss_availability },
            parseDateStrict(nextOpening, DISPLAY_DATE),
            ),
          });
          noMoreAvailability = determineDeliveryNoAvailability(totalDays, nextOpenings);
        }
      }
      nextOpening = '';
      noMoreAvailability = determineDeliveryNoAvailability(totalDays, nextOpenings);
    }
    nextOpeningDeterminedCrews.push({ ...c, nextOpenings });
  });
  return nextOpeningDeterminedCrews.filter(c => c.nextOpenings.length > 0);
};

const getSuggestionNextOpenings = (crews, date, phaseData) => {
  const { zip_code, surfaceId, isCaliBamboo, needDelivery } = phaseData;
  let buffer = 0;
  if (needDelivery) {
    if (surfaceId === 1) {
      buffer = isCaliBamboo ? DELIVERY_BUFFERS[surfaceId][1] : DELIVERY_BUFFERS[surfaceId][0];
    } else buffer = DELIVERY_BUFFERS[surfaceId][0];
  }
  let defaultMoment = moment(date);
  if (needDelivery) defaultMoment = defaultMoment.add(buffer, 'days');
  const givenDate = defaultMoment.format(DISPLAY_DATE);
  const nextOpeningDeterminedCrews = [];
  crews.filter(c => !c.assignee.delivery_dedicated).forEach((c) => {
    const { projectedJobDays, projectedJobLength, twd, ss_availability, assignee } = c;
    const nextOpenings = [];
    const startDate = parseDateStrict(givenDate, DISPLAY_DATE);
    let noMoreAvailability = false;
    while (!noMoreAvailability && nextOpenings.length < 10) {
      if (nextOpenings.length > 0) startDate.add(1, 'day');
      const newDate = startDate.clone();
      let totalDays = 0;
      let nextOpening = '';
      let availableDays = 0;
      while (totalDays < TOTAL_DAY_LIMIT && availableDays < projectedJobDays) {
        totalDays += 1;
        if (isDayCovered(
            { ...ss_availability, twd },
            { newDate, openings: nextOpenings.map(o => o.opening) },
            { ...assignee, projectedJobDays, projectedJobLength },
            phaseData)) {
          nextOpening = '';
          newDate.add(1, 'day');
          availableDays = 0;
        } else if (!nextOpening) {
          nextOpening = newDate.format(DISPLAY_DATE);
          newDate.add(1, 'day');
          availableDays += 1;
        } else {
          newDate.add(1, 'day');
          availableDays += 1;
        }
      }
      if (totalDays <= TOTAL_DAY_LIMIT) {
        if (noMoreTwd(newDate, twd, zip_code)) noMoreAvailability = true;
        if (!noMoreAvailability) {
          nextOpenings.push({
            type: 'install',
            opening: nextOpening,
            sameDayAppt: findSameDayExisting(nextOpening, ss_availability.appts),
            startTime: getSuggestionStartTime(
              parseDateStrict(nextOpening, DISPLAY_DATE),
              assignee.default_start_time,
              projectedJobDays,
              projectedJobLength,
              ss_availability.appts,
            ),
          });
          if (totalDays === TOTAL_DAY_LIMIT) noMoreAvailability = true;
        }
      }
      nextOpening = '';
      availableDays = 0;
      if (!noMoreAvailability) noMoreAvailability = totalDays > TOTAL_DAY_LIMIT;
    }
    nextOpeningDeterminedCrews.push({ ...c, nextOpenings });
  });
  return nextOpeningDeterminedCrews;
};

const hasDailyCapacity = sfPerDay => sfPerDay > 0;

const determineZeroCapacity = (crewSpecifics, pendingSurface) => {
  const { hardwood_sf_per_day, carpet_sf_per_day, tile_sf_per_day, laminate_sf_per_day,
    vinyl_sf_per_day, vinyl_plank_sf_per_day, backsplash_sf_per_day } = crewSpecifics;
  switch (pendingSurface) {
    case 'Hardwood':
      return !hasDailyCapacity(hardwood_sf_per_day || 0);
    case 'Carpet':
      return !hasDailyCapacity(carpet_sf_per_day || 0);
    case 'Tile':
      return !hasDailyCapacity(tile_sf_per_day || 0);
    case 'Laminate':
      return !hasDailyCapacity(laminate_sf_per_day || 0);
    case 'Vinyl':
      return !hasDailyCapacity(vinyl_sf_per_day || 0);
    case 'Vinyl Plank':
      return !hasDailyCapacity(vinyl_plank_sf_per_day || 0);
    case 'Backsplash':
      return !hasDailyCapacity(backsplash_sf_per_day || 0);
    default:
      return false;
  }
};

const determineMismatchExists = (phaseSpecificsToMatch, assignee, pendingSurface, needDelivery) => {
  const { crew_specific, delivery_dedicated } = assignee;
  let specificMismatch = false;
  if (needDelivery && delivery_dedicated) specificMismatch = false;
  else if (crew_specific.phone_call_needed || determineZeroCapacity(
    crew_specific, pendingSurface,
  ) || delivery_dedicated) specificMismatch = true;
  else {
    const values = [];
    phaseSpecificsToMatch.forEach(s => values.push(crew_specific[RELATED_SPECIFIC_FIELDS[s]]));
    specificMismatch = values.some(v => !v);
  }
  return { ...assignee, specificMismatch };
};

const removeMismatches = (
  crews, phaseSpecificsToMatch, pendingSurface, keepMismatches, needDelivery = false,
) => {
  const mismatchDeterminedCrews = [];
  crews.forEach(c => mismatchDeterminedCrews.push({
    ...c,
    assignee: determineMismatchExists(
      phaseSpecificsToMatch, c.assignee, pendingSurface, needDelivery,
    ),
  }));
  if (keepMismatches) return mismatchDeterminedCrews;
  return mismatchDeterminedCrews.filter(m => m.assignee.specificMismatch === false);
};

const sortCrews = crews => sortBy(crews, [
  c => memberHasMismatch(c),
  c => (memberHasMismatch(c) ? true : c.projectedJobDays > 1),
  (c) => {
    if (memberHasMismatch(c)) return true;
    if (c.projectedJobDays > 1) return c.projectedJobLength;
    return WORK_DAY_HOURS - c.projectedJobLength;
  },
  c => (memberHasMismatch(c) ? true : c.nextOpening),
  c => (memberHasMismatch(c) ? true : c.assignee.name),
]);

/*
 * TODO: implement https://babeljs.io/docs/en/babel-plugin-proposal-pipeline-operator
 * (https://github.com/tc39/proposal-pipeline-operator)
 */
export const reorderResultsAndPrioritizeCrews = (members, pendingAppt, defaultCalStart, isCcs) => {
  const { phase: { phase_specific, surface_type: { name } } } = pendingAppt;
  // separating the crews from the non-crews, return if no crews
  const partitionedMembers = separateCrews(members);
  if (!partitionedMembers[0].length) return partitionedMembers[1];
  // what fields on the crew specifics do we care about based on the phase?
  const matchingSpecifics = getSpecificsToMatch(phase_specific);
  // adding a boolean field to each crew-type member indicating if there is a mismatch
  const mismatchDeterminedCrews = removeMismatches(
    partitionedMembers[0], matchingSpecifics, name, isCcs === false,
  );
  // separating mismatched crews so that we do not unnecessarily run more ss logic on them
  const partitionedCrews = separateMismatches(mismatchDeterminedCrews);
  // if we have no matching crews, no need to try and run all this logic
  let finalCrews = [];
  if (partitionedCrews[1].length > 0) {
    // determining how long a job will take based on given specifics
    const projectedJobLengthDeterminedCrews = getCalJobLengthAttrs(
      partitionedCrews[1], matchingSpecifics, pendingAppt,
    );
    // determine next available window of time for new appt based on projected time required
    const nextOpeningDeterminedCrews = getNextOpeningAttr(
      projectedJobLengthDeterminedCrews, defaultCalStart,
    );
    // adding back mismatch crews if there are any
    finalCrews = partitionedCrews[0].length > 0 ?
      partitionedCrews[0].concat(nextOpeningDeterminedCrews) :
      nextOpeningDeterminedCrews;
  } else finalCrews = partitionedCrews[0];
  // adding a boolean field to each crew-type member indicating if there is an overlapping date
  const sortedCrews = sortCrews(getOverlappingDatesAttrs(finalCrews, pendingAppt));
  // adding map matrix field to existing appts on same day as pending appts
  if (partitionedMembers[1].length > 0) return sortedCrews.concat(partitionedMembers[1]);
  return sortedCrews;
};

const sortDeliverySuggestions = suggestions => sortBy(suggestions, [
  s => moment(s.opening, DISPLAY_DATE),
  s => s.assignee.priority,
  s => s.assignee.name,
]);

const sortInstallSuggestions = suggestions => sortBy(suggestions, [
  s => s.projectedJobDays > 1,
  s => (s.projectedJobDays > 1 ? s.projectedJobLength : false),
  s => moment(s.opening, DISPLAY_DATE),
  s => (s.projectedJobDays <= 1 ? WORK_DAY_HOURS -
    Math.ceil(s.sameDayAppt.projected_job_length || 0) -
    s.projectedJobLength : true),
  s => s.assignee.name,
]);

export const sortSuggestions = (suggestions, type = 'install') => (type === 'install' ?
  sortInstallSuggestions(suggestions) : sortDeliverySuggestions(suggestions));

export const groupSuggestionsByPage = (suggestions, denominator = 5) => {
  const diviedSuggestions = [];
  while (suggestions.length > 0) diviedSuggestions.push(suggestions.splice(0, denominator));
  return diviedSuggestions;
};

const invalidTime = (start, end) => [start, end].includes('Invalid date');

const buildSuggestions = (crews, isDelivery = false) => {
  const suggestions = [];
  crews.forEach((c) => {
    const { nextOpenings = [], assignee, projectedJobDays = null, projectedJobLength = null } = c;
    nextOpenings.forEach((o) => {
      const suggestion = {
        ...o,
        key: `${assignee.name + o.opening}`,
        assignee,
        projectedJobDays,
        projectedJobLength,
        projectedCompletion: null,
        endTime: getEndTimeString(o.startTime, isDelivery),
      };
      if (!isDelivery && !invalidTime(o.startTime, o.endTime)) {
        suggestion.projectedCompletion = determineProjectedCompletion(
          projectedJobLength,
          projectedJobDays,
          o.opening,
          o.startTime,
          c.assignee.default_start_time,
        );
      }
      suggestions.push(suggestion);
    });
  });
  const sortedSuggestions = suggestions.length > 0 ? sortSuggestions(
    suggestions.filter(s => (s.opening || '').length)
               .filter(s => !invalidTime(s.startTime, s.endTime),
  ), (isDelivery ? 'delivery' : 'install')).slice(0, 20) : [];
  return {
    suggestions: sortedSuggestions,
    suggestionCount: sortedSuggestions.length || 0,
  };
};

const deliveryStoresValid = (sameDayAppt, storeId) => {
  const numAppts = sameDayAppt.length;
  if (!numAppts || numAppts === 2) return true;
  if (numAppts >= 4) return false;
  if ([1, 3].includes(numAppts)) return sameDayAppt[numAppts - 1].phase.store.id === storeId;
  return false;
};

const pruneSuggestionsByDistance = (suggestions, storeId) => sortBy(suggestions, [
  s => moment(s.opening, DISPLAY_DATE),
]).filter(s =>
  isEmpty(s.sameDayAppt) ||
  isEmpty(s.mapMatrix) ||
  ((s.type === 'install' || deliveryStoresValid(s.sameDayAppt, storeId)) && (
  s.mapMatrix.duration <= 0 ||
  s.mapMatrix.duration / 60 <= (s.assignee.crew_specific || {}).max_travel_time || 0)));

export const getInstallSuggestions = (crews, phase, defaultCalStart, relatedPhases) => {
  const { id: phaseId, type, zip_code, sqft, spig_delivery, phase_specific, source,
          phase_specific: { is_cali_bamboo_hardwood: isCaliBamboo },
          surface_type: { id: surfaceId, name: surfaceType },
          store: { id: storeId, delivery_method: deliveryMethod } } = phase;
  const needDelivery = isDeliveryPhase(type, surfaceType, deliveryMethod, spig_delivery);
  const specificsToMatch = getSpecificsToMatch(phase_specific);
  const matchingCrews = removeMismatches(crews, specificsToMatch, surfaceType, false, needDelivery);
  const crewsWithEstimates = getSuggestionJobLengthAttrs(
    matchingCrews,
    specificsToMatch,
    phase,
  );
  const installSuggestions = buildSuggestions(getSuggestionNextOpenings(
    crewsWithEstimates,
    defaultCalStart,
    { phaseId,
      zip_code,
      sqft,
      relatedPhases,
      isCaliBamboo,
      surfaceId,
      needDelivery,
      source },
  ));
  const deliverySuggestions = needDelivery ? buildSuggestions(getSuggestionDeliveryOpenings(
    crewsWithEstimates,
    defaultCalStart,
    { phaseId, zip_code, surfaceType, sqft, relatedPhases, source },
  ), true) : {};
  return getSuggestionMapAttr(installSuggestions, deliverySuggestions, phaseId, storeId)
    .then((res) => {
      const finalDeliverySuggestions = needDelivery ?
        (pruneSuggestionsByDistance(res.deliverySuggestions, storeId) || []) : [];
      const finalSuggestions = (res.suggestions || []).length ?
        sortSuggestions(pruneSuggestionsByDistance(res.suggestions, storeId)) : [];
      return {
        deliverySuggestions: finalDeliverySuggestions,
        deliveryCount: finalDeliverySuggestions.length,
        suggestions: finalSuggestions,
        suggestionCount: finalSuggestions.length,
      };
    });
};



// WEBPACK FOOTER //
// ./client/app/utils/schedulingUtils.js